import { useState, useMemo } from 'react';
import { fmt$ } from '../utils/formatters';
import InspectName from './InspectName';

// Sort categories alphabetically with Other/Needs Review always last
function sortCats(cats){const tail=['Other','Needs Review'];return cats.filter(c=>!tail.includes(c)).sort().concat(tail.filter(c=>cats.includes(c)));}

export default function OrderGuide({t, si, sb, sh, sd, mn, ings, batches, mi, orgId, ogDist, setOgDist, invCounts, upIngF, settings, setPg, setReturnTo, save, setIngs}) {
const[onHand,setOnHand]=useState({});
const[edited,setEdited]=useState({});
const[collapsed,setCollapsed]=useState({});

// All active ingredients (is_active true or null/undefined)
const activeIngs=useMemo(()=>ings.filter(i=>i.active!==false),[ings]);

// Vendor list from Settings + any ingredient distributors not in Settings
const settingsVendors=useMemo(()=>settings?.vendors||[],[settings]);
const unrecognizedDists=useMemo(()=>{
  const sv=new Set(settingsVendors.map(v=>v.toUpperCase()));
  return [...new Set(activeIngs.map(i=>i.dist).filter(d=>d&&!sv.has(d.toUpperCase())))].sort();
},[activeIngs,settingsVendors]);
const dists=useMemo(()=>[...settingsVendors.sort(),...(unrecognizedDists.length?unrecognizedDists:[])]
,[settingsVendors,unrecognizedDists]);

// Related items: when filtering by a vendor, also show items from other vendors
// that are commonly ordered alongside (e.g. boba straws with Q Bubble products)
const RELATED_KEYWORDS={'Q BUBBLE':['BOBA','BUBBLE','TAPIOCA']};

// Filtered by distributor (case-insensitive match) + related items
const filtered=useMemo(()=>{
  let items=activeIngs;
  if(ogDist!=="All"){
    const uc=ogDist.toUpperCase();
    const keywords=RELATED_KEYWORDS[uc]||[];
    items=items.filter(i=>{
      if(i.dist?.toUpperCase()===uc)return true;
      // Include related items from other vendors
      if(keywords.length){const nm=i.n.toUpperCase();return keywords.some(k=>nm.includes(k));}
      return false;
    });
  }
  return items;
},[activeIngs,ogDist]);
const unrecognizedSet=useMemo(()=>new Set(unrecognizedDists.map(d=>d.toUpperCase())),[unrecognizedDists]);


// Group by category, sort within each
const grouped=useMemo(()=>{
  const g={};
  for(const ig of filtered){
    const cat=ig.cat||"Other";
    if(!g[cat])g[cat]=[];
    g[cat].push(ig);
  }
  // Sort within each category
  // When filtered to a vendor, use product-type grouping so like items are together
  function itemSortKey(ig){
    const u=ig.n.toUpperCase();
    if(u.includes('POWDER'))return 0;
    if(u.includes('SYRUP')||u.includes('MOLASSES'))return 1;
    if(u.includes('BOBA'))return 2;
    if(u.includes('TAPIOCA'))return 3;
    if(u.includes('TEA')||u.includes('MATCHA'))return 4;
    return 5;
  }
  for(const cat in g){
    if(ogDist!=='All'){
      // Vendor view: group by product type, then alphabetical
      g[cat].sort((a,b)=>{const ka=itemSortKey(a),kb=itemSortKey(b);if(ka!==kb)return ka-kb;return a.n.localeCompare(b.n);});
    } else {
      g[cat].sort((a,b)=>a.n.localeCompare(b.n));
    }
  }
  return g;
},[filtered,ogDist]);

const catOrder=sortCats(Object.keys(grouped).filter(c=>grouped[c]?.length));

function countToOH(id){
  const cnt=invCounts[id];
  if(!cnt)return null;
  const cases=parseFloat(cnt.cases)||0;
  const open=parseFloat(cnt.open)||0;
  const ig=ings.find(x=>x.id===id);
  const ppc=ig?.ppc||1;
  // open qty as fractional cases (same ratio Inventory uses for value: open * cp/ppc)
  return Math.round((cases+open/ppc)*100)/100;
}
function getOH(id){
  if(onHand[id]!==undefined)return onHand[id];
  const v=countToOH(id);
  return v!==null?v:0;
}
function hasCount(id){return invCounts[id]!==undefined;}

// Summary stats
const totalItems=filtered.length;
const needOrder=filtered.filter(ig=>getOH(ig.id)<ig.par).length;
const totalOrderCost=filtered.reduce((s,ig)=>{const gap=Math.max(0,ig.par-getOH(ig.id));return s+gap*ig.cp;},0);

return(<div>
<h1 style={{fontSize:22,fontWeight:700,margin:"0 0 6px"}}>Order Guide</h1>
<p style={{color:t.tm,fontSize:12,margin:"0 0 16px"}}>All active ingredients grouped by category. Edit On Hand to calculate order quantities.</p>

{/* Filters */}
<div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
<select value={ogDist} onChange={e=>setOgDist(e.target.value)} style={{...si,width:220}}>
<option value="All">All Distributors</option>
{settingsVendors.sort().map(d=><option key={d} value={d}>{d}</option>)}
{unrecognizedDists.length>0&&<option disabled>──────────</option>}
{unrecognizedDists.map(d=><option key={d} value={d}>{d} (not in Settings)</option>)}
</select>
<div style={{flex:1}}/>
<div style={{display:"flex",gap:14,fontSize:12}}>
<span style={{color:t.tm}}>{totalItems} items</span>
<span style={{color:needOrder>0?t.ac:t.gn,fontWeight:600}}>{needOrder} need ordering</span>
<span style={{fontFamily:mn,fontWeight:600,color:t.yl}}>Est. ${fmt$(totalOrderCost)}</span>
</div>
</div>

{/* Category sections */}
{catOrder.map(cat=>{
const items=grouped[cat];
const isCollapsed=collapsed[cat];
const catNeedOrder=items.filter(ig=>getOH(ig.id)<ig.par).length;
const catOrderCost=items.reduce((s,ig)=>{const gap=Math.max(0,ig.par-getOH(ig.id));return s+gap*ig.cp;},0);

return(<div key={cat} style={{marginBottom:12}}>
{/* Category header */}
<button onClick={()=>setCollapsed(p=>({...p,[cat]:!p[cat]}))} style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",padding:"10px 14px",background:t.sf,border:`1px solid ${t.bd}`,borderRadius:isCollapsed?8:"8px 8px 0 0",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
<div style={{display:"flex",alignItems:"center",gap:10}}>
<span style={{fontSize:11,color:t.td,width:16}}>{isCollapsed?"▸":"▾"}</span>
<span style={{fontSize:13,fontWeight:600,color:t.tx}}>{cat}</span>
<span style={{fontSize:10,color:t.td}}>({items.length})</span>
</div>
<div style={{display:"flex",gap:14,fontSize:10}}>
{catNeedOrder>0&&<span style={{color:t.ac,fontWeight:600}}>{catNeedOrder} to order</span>}
{catOrderCost>0&&<span style={{fontFamily:mn,color:t.yl}}>${fmt$(catOrderCost)}</span>}
</div>
</button>

{/* Items table */}
{!isCollapsed&&<div style={{background:t.cd,border:`1px solid ${t.bd}`,borderTop:"none",borderRadius:"0 0 8px 8px",overflow:"hidden"}}>
<table style={{width:"100%",borderCollapse:"collapse"}}>
<thead><tr style={{background:t.sf}}>
{["Product","VIN","Dist","Case Qty","On Hand","Par","Order Qty","Case $","Order $"].map(h=>
<th key={h} style={{...sh,fontSize:9}}>{h}</th>)}
</tr></thead>
<tbody>{items.map(ig=>{
const oh=getOH(ig.id);
const gap=Math.max(0,ig.par-oh);
const needsOrder=oh<ig.par;
return(<tr key={ig.id} style={{borderBottom:`1px solid ${t.bd}`,background:needsOrder?"rgba(200,55,45,0.04)":"transparent"}} onMouseOver={e=>e.currentTarget.style.background=needsOrder?"rgba(200,55,45,0.08)":t.sfh} onMouseOut={e=>e.currentTarget.style.background=needsOrder?"rgba(200,55,45,0.04)":"transparent"}>
<td style={{...sd,fontWeight:500,fontSize:12}}><InspectName type="ingredient" item={ig} ings={ings} batches={batches} mi={mi} orgId={orgId} t={t} save={save} setIngs={setIngs} settings={settings} si={si} sb={sb} mn={mn}>{ig.n}</InspectName></td>
<td style={{...sd,fontSize:10,color:t.td,fontFamily:mn}}>{ig.vin||"—"}</td>
<td style={sd}>{(()=>{const isRelated=ogDist!=='All'&&ig.dist?.toUpperCase()!==ogDist.toUpperCase();return(<><span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:isRelated?"rgba(139,143,158,0.15)":ig.dist?.toUpperCase()==="DENNIS"?"rgba(59,130,246,0.12)":unrecognizedSet.has(ig.dist?.toUpperCase())?"rgba(212,168,36,0.15)":t.sfh,color:isRelated?t.tm:ig.dist?.toUpperCase()==="DENNIS"?t.bl:unrecognizedSet.has(ig.dist?.toUpperCase())?t.yl:t.tm,fontStyle:isRelated?"italic":"normal"}}>{ig.dist}{unrecognizedSet.has(ig.dist?.toUpperCase())?" *":""}</span>{isRelated&&<div style={{fontSize:8,color:t.td,marginTop:2,fontStyle:"italic"}}>order from {ig.dist}</div>}</>);})()}</td>
<td style={{...sd,fontFamily:mn,fontSize:11,color:t.td}}>{ig.cq} {ig.cu}</td>
<td style={sd}><div><input type="number" value={onHand[ig.id]!==undefined?onHand[ig.id]:(countToOH(ig.id)!==null?countToOH(ig.id):"")} onChange={e=>{const v=e.target.value;setOnHand(p=>({...p,[ig.id]:v===""?0:parseFloat(v)||0}));setEdited(p=>({...p,[ig.id]:true}));}} style={{...si,width:55,padding:"4px 6px",textAlign:"center",fontFamily:mn,fontSize:12,background:needsOrder?"rgba(200,55,45,0.06)":t.sf}} min="0"/>{hasCount(ig.id)&&!edited[ig.id]&&onHand[ig.id]===undefined&&<div style={{fontSize:8,color:t.td,textAlign:"center",marginTop:1}}>today's count</div>}</div></td>
<td style={sd}><input type="number" value={ig.par} onChange={e=>upIngF(ig.id,"par",e.target.value)} style={{...si,width:55,padding:"4px 6px",textAlign:"center",fontFamily:mn,fontSize:12}} min="0"/></td>
<td style={{...sd,fontFamily:mn,fontWeight:600,fontSize:13,color:needsOrder?t.ac:t.gn}}>{needsOrder?gap:"OK"}</td>
<td style={{...sd,fontFamily:mn,fontSize:11}}>${fmt$(ig.cp)}</td>
<td style={{...sd,fontFamily:mn,fontWeight:600,color:needsOrder?t.ac:t.td}}>{needsOrder?"$"+fmt$(gap*ig.cp):"—"}</td>
</tr>);})}</tbody></table>
</div>}
</div>);
})}

{catOrder.length===0&&<div style={{textAlign:"center",padding:40,color:t.td,fontSize:13}}>No ingredients found{ogDist!=="All"?" for "+ogDist:""}</div>}
</div>);
}
